import { Button, Typography } from "@mui/material";
import { currency } from "const/constants";

export const money = (value) => `${currency}${Number(value || 0).toFixed(2)}`;

const TONE_CLASS = {
  good: "text-emerald-700",
  warn: "text-amber-700",
  bad: "text-rose-700",
  neutral: "text-app-text",
};

const TILE_BORDER = {
  good: "border-emerald-300 bg-emerald-50",
  warn: "border-amber-300 bg-amber-50",
  bad: "border-rose-300 bg-rose-50",
  neutral: "border-app-border bg-app-surface-muted/40",
};

/**
 * Hint for a Due tile: only worth showing when the terms reached so far owe something different.
 * Credit is compared as 0 owed, since the tile already labels it Overpaid.
 */
export const effectiveDueHint = (due, effectiveDue) => {
  if (effectiveDue == null) return undefined;
  const owedToday = Math.max(0, Number(effectiveDue) || 0);
  const owed = Math.max(0, Number(due) || 0);
  return Math.abs(owedToday - owed) >= 0.005 ? `Effective due ${money(owedToday)}` : undefined;
};

export const AccountMetricTile = ({
  label,
  value,
  tone = "neutral",
  size = "sm",
  emphasize = false,
  hint,
  /** Makes the tile a button (e.g. open edit figures / payments). */
  onClick,
  actionHint,
}) => {
  const valueClass = size === "lg" ? "text-base" : "text-sm";
  const interactive = Boolean(onClick);
  const className = `rounded-lg border px-3 py-2.5 min-w-0 text-left w-full ${
    emphasize
      ? TILE_BORDER[tone] || TILE_BORDER.neutral
      : "border-app-border/70 bg-app-surface-muted/40"
  } ${
    interactive
      ? "cursor-pointer hover:border-primary/50 hover:bg-primary/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary transition-colors"
      : ""
  }`;

  const body = (
    <>
      <div className="flex items-start justify-between gap-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
          {label}
        </p>
        {interactive ? (
          <span className="text-[10px] font-semibold text-primary shrink-0 leading-none mt-0.5">
            {actionHint === "Payments" ? "Pay" : "Edit"}
          </span>
        ) : null}
      </div>
      <p
        className={`${valueClass} font-semibold m-0 mt-0.5 truncate ${
          TONE_CLASS[tone] || TONE_CLASS.neutral
        }`}
      >
        {value}
      </p>
      {hint ? <p className="text-[10px] text-app-text-muted m-0 mt-0.5 truncate">{hint}</p> : null}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        title={actionHint || `Edit ${label}`}
        aria-label={actionHint ? `${actionHint}: ${label}` : `Edit ${label}`}
      >
        {body}
      </button>
    );
  }

  return <div className={className}>{body}</div>;
};

export const AccountSummaryStrip = ({ items = [] }) => {
  if (!items.length) return null;
  const cols =
    items.length >= 4
      ? "grid-cols-2 lg:grid-cols-4"
      : items.length === 3
      ? "grid-cols-1 sm:grid-cols-3"
      : "grid-cols-2";
  return (
    <div className={`grid gap-2 ${cols}`}>
      {items.map((item) => (
        <AccountMetricTile key={item.label} {...item} size="sm" emphasize />
      ))}
    </div>
  );
};

export const AccountPanelHeader = ({ title, description, count, action }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0 max-w-2xl">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-app-text m-0">{title}</h3>
        {typeof count === "number" && count > 0 ? (
          <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold bg-app-surface-muted text-app-text-secondary border border-app-border">
            {count}
          </span>
        ) : null}
      </div>
      {/* {description ? (
        <p className="text-xs text-app-text-secondary m-0 mt-1 max-w-xl leading-relaxed">
          {description}
        </p>
      ) : null} */}
    </div>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
);

export const AccountEmptyState = ({ title, description, actionLabel, onAction, actionIcon }) => (
  <div className="rounded-xl border border-dashed border-app-border px-4 py-10 text-center">
    <Typography className="text-sm font-semibold text-app-text mb-1">{title}</Typography>
    {description ? (
      <Typography color="text.secondary" className="mb-3 max-w-md mx-auto">
        {description}
      </Typography>
    ) : null}
    {onAction && actionLabel ? (
      <Button variant="contained" startIcon={actionIcon} onClick={onAction}>
        {actionLabel}
      </Button>
    ) : null}
  </div>
);

export const dueTone = (due) => {
  const n = Number(due) || 0;
  if (n < 0) return "good";
  if (n > 0) return "warn";
  return "neutral";
};

export const profitTone = (profit) => ((Number(profit) || 0) >= 0 ? "good" : "bad");

export const dueLabel = (due) => ((Number(due) || 0) < 0 ? "Overpaid" : "Due");
export const profitLabel = (profit) => ((Number(profit) || 0) >= 0 ? "Profit" : "Loss");
export const absMoney = (value) => money(Math.abs(Number(value) || 0));

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Says how many of the terms in an admission's totals are still waiting to be imported. */
export const termCountLabel = ({ imported = 0, notImported = 0 }) =>
  [plural(imported, "imported term"), notImported > 0 ? `${notImported} not imported yet` : null]
    .filter(Boolean)
    .join(", ");
